import React, { useEffect, useRef } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Layout } from '@components';
import { usePrefersReducedMotion } from '@hooks';

const StyledMain = styled.main`
  max-width: 1000px;

  header {
    margin-bottom: 50px;
  }

  .list {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const StyledItem = styled.li`
  ${({ theme }) => theme.mixins.boxShadow};
  background-color: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 1.75rem;

  h2 {
    margin: 0 0 6px;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
  }

  .meta {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin-bottom: 12px;
  }

  .desc {
    color: var(--light-slate);
    font-size: 17px;
    line-height: 1.6;
  }
`;

const CertificationsPage = ({ location, data }) => {
  const items = data.certifications.edges;
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <Layout location={location}>
      <Helmet
        title="Certifications"
        meta={[
          {
            name: 'description',
            content:
              'Professional certifications and technical courses completed across software engineering and cloud technologies.',
          },
        ]}
      />

      <StyledMain ref={revealContainer}>
        <header>
          <h1 className="big-heading">Certifications</h1>
          <p className="subtitle">Certificates, and courses I&apos;ve earned along the way</p>
        </header>

        <ul className="list">
          {items.map(({ node }) => {
            const { title, institution, issued, credentialUrl } = node.frontmatter;
            return (
              <StyledItem key={node.id}>
                <h2>
                  {credentialUrl ? (
                    <a
                      className="inline-link"
                      href={credentialUrl}
                      target="_blank"
                      rel="noreferrer">
                      {title}
                    </a>
                  ) : (
                    title
                  )}
                </h2>

                <div className="meta">
                  {institution && <span>{institution}</span>}
                  {institution && issued && <span> · </span>}
                  {issued && <span>{issued}</span>}
                </div>

                <div className="desc" dangerouslySetInnerHTML={{ __html: node.html }} />
              </StyledItem>
            );
          })}
        </ul>
      </StyledMain>
    </Layout>
  );
};

CertificationsPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.shape({
    certifications: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string.isRequired,
            html: PropTypes.string.isRequired,
            frontmatter: PropTypes.shape({
              title: PropTypes.string.isRequired,
              institution: PropTypes.string,
              issued: PropTypes.string,
              credentialUrl: PropTypes.string,
            }).isRequired,
          }).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

export default CertificationsPage;

export const pageQuery = graphql`
  {
    certifications: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/user/certifications/" } }
      sort: { fields: [frontmatter___order, frontmatter___title], order: DESC }
    ) {
      edges {
        node {
          id
          html
          frontmatter {
            title
            institution
            issued
            credentialUrl
            order
          }
        }
      }
    }
  }
`;
